import { Headset, Phone, PhoneCall, PhoneOff } from 'lucide-react'
import { useState } from 'react'
import { Link, useFetcher } from 'react-router'
import { AGENT_STATE_LABEL } from '#app/routes/admin/centre-appel/+shared/agent-state.tsx'
import { type action as presenceAction } from '#app/routes/resources/agent-presence.ts'
import { type AgentPresence } from '#app/utils/agent-presence.server.ts'
import { cn } from '#app/utils/misc.tsx'
import { formatPhone } from '#app/utils/phone.ts'
import { useSoftphone } from './softphone/softphone-provider.tsx'
import { SoftphoneToggle } from './softphone/softphone-toggle.tsx'
import { Button } from './ui/button.tsx'
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from './ui/sheet.tsx'

/**
 * Header quick-menu for a member linked to a call-center agent: their live
 * state at a glance (dot on the phone icon), a one-tap presence toggle, the
 * browser-softphone switch, and a link to their agent profile. `presence`
 * comes from the root loader (cached a few seconds server-side) and is
 * `null` when the API is down.
 */

const DOT_CLASS: Record<AgentPresence['state'], string> = {
	available: 'bg-green-600',
	ringing: 'bg-sky-500 animate-pulse',
	on_call: 'bg-amber-500',
	offline: 'bg-muted-foreground/50',
}

function stateOf(
	presence: AgentPresence | null,
): AgentPresence['state'] | null {
	if (!presence || !presence.isActive) return null
	return presence.state
}

export function AgentPresenceMenu({
	presence,
}: {
	presence: AgentPresence | null
}) {
	const [open, setOpen] = useState(false)
	const fetcher = useFetcher<typeof presenceAction>()
	const softphone = useSoftphone()

	const state = stateOf(presence)
	// The live softphone call wins over the (few-seconds-stale) server state.
	const inCall =
		softphone.call !== null || state === 'ringing' || state === 'on_call'
	const optimisticOnline =
		fetcher.state !== 'idle' && fetcher.formData
			? fetcher.formData.get('isOnline') === 'true'
			: (presence?.isOnline ?? false)
	const error =
		fetcher.state === 'idle' && fetcher.data && !fetcher.data.ok
			? fetcher.data.error
			: null

	// What the dot shows: an in-flight toggle lands on available/offline.
	const dotState: AgentPresence['state'] | null =
		state === null
			? null
			: inCall
				? softphone.call
					? softphone.call.phase === 'active'
						? 'on_call'
						: 'ringing'
					: state
				: optimisticOnline
					? 'available'
					: 'offline'

	const TriggerIcon =
		dotState === null
			? Phone
			: inCall
				? PhoneCall
				: dotState === 'offline'
					? PhoneOff
					: Phone
	const label =
		dotState === null
			? "Centre d'appel"
			: `Centre d'appel : ${AGENT_STATE_LABEL[dotState].toLowerCase()}`

	const ringsHere = softphone.status === 'ready'

	return (
		<Sheet open={open} onOpenChange={setOpen}>
			<button
				type="button"
				onClick={() => setOpen(true)}
				aria-label={label}
				aria-haspopup="dialog"
				aria-expanded={open}
				className="focus-visible:ring-ring text-muted-foreground hover:text-foreground h-touch w-touch relative inline-flex cursor-pointer touch-manipulation items-center justify-center rounded-full outline-hidden transition-transform focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-[0.95]"
			>
				<TriggerIcon className="h-[22px] w-[22px]" aria-hidden="true" />
				{dotState !== null && (
					<span
						className={cn(
							'ring-background absolute top-2 right-2 h-2.5 w-2.5 rounded-full ring-2',
							DOT_CLASS[dotState],
						)}
						aria-hidden="true"
					/>
				)}
			</button>

			<SheetContent side="bottom" className="gap-0 rounded-t-none border-0 p-0">
				<SheetHeader className="border-border border-b p-4 pr-12">
					<p className="text-plq-rouge flex items-center gap-2 text-[11px] font-semibold tracking-[0.15em] uppercase">
						<span
							className="bg-plq-rouge h-1.5 w-1.5 rounded-full"
							aria-hidden="true"
						/>
						Centre d'appel
					</p>
					<SheetTitle className="flex items-center gap-2.5 text-xl font-bold">
						{dotState !== null && (
							<span
								className={cn(
									'h-3 w-3 shrink-0 rounded-full',
									DOT_CLASS[dotState],
								)}
								aria-hidden="true"
							/>
						)}
						{presence === null
							? 'État indisponible'
							: !presence.isActive
								? 'Accès désactivé'
								: AGENT_STATE_LABEL[dotState ?? 'offline']}
					</SheetTitle>
					<SheetDescription>
						{presence === null
							? "Impossible de joindre le centre d'appel pour le moment."
							: !presence.isActive
								? 'Votre fiche d’agent a été désactivée par un responsable.'
								: ringsHere
									? `Les appels sonnent dans ce navigateur, puis au ${formatPhone(presence.phone)}.`
									: `Les appels sonnent au ${formatPhone(presence.phone)}.`}
					</SheetDescription>
				</SheetHeader>

				<div className="flex flex-col gap-4 p-4">
					{presence?.isActive && (
						<fetcher.Form method="post" action="/resources/agent-presence">
							<input
								type="hidden"
								name="isOnline"
								value={optimisticOnline ? 'false' : 'true'}
							/>
							<Button
								type="submit"
								variant={optimisticOnline ? 'outline' : 'default'}
								className="h-14 w-full text-base"
								disabled={fetcher.state !== 'idle'}
								aria-pressed={optimisticOnline}
							>
								{optimisticOnline
									? inCall
										? 'Ne plus recevoir d’appels après celui-ci'
										: 'Me mettre hors ligne'
									: 'Je suis disponible'}
							</Button>
							{error && (
								<p role="alert" className="text-destructive mt-2 text-sm">
									{error}
								</p>
							)}
						</fetcher.Form>
					)}

					{presence?.isActive && (
						<SoftphoneToggle className="border-border border-t pt-4" />
					)}

					<SheetClose asChild>
						<Link
							to="/settings/profile/centre-appel"
							prefetch="intent"
							className="hover:bg-muted text-foreground min-h-touch flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors"
						>
							<Headset className="h-5 w-5 shrink-0" aria-hidden="true" />
							Mon profil d'agent, composer un numéro et mes appels
						</Link>
					</SheetClose>
				</div>
			</SheetContent>
		</Sheet>
	)
}
